import { useRef, useState, type ReactNode } from "react";
import { MAX_IMPORT_BYTES } from "../constants";
import { CURRENCY_SYMBOLS, transfer as copy } from "../content";
import { applyImport, describe, downloadJson, importErrorText, mergeById, parseIncoming, toPayload } from "../transfer";
import type { Data } from "../types";

interface Props {
  data: Data;
  /** Called with the new data; the importer has already confirmed with the person. */
  onImport: (data: Data) => void;
}

type Banner =
  | { kind: "confirm"; incoming: Data; source: string }
  | { kind: "note" | "error"; text: string }
  | null;

/**
 * Export/Import state. `controls` are the header buttons; `panels` is the
 * confirmation banner and import panel, which belong below the header.
 */
export function useDataTransfer({ data, onImport }: Props): { controls: ReactNode; panels: ReactNode } {
  const [panelOpen, setPanelOpen] = useState(false);
  const [status, setStatus] = useState("");
  const [banner, setBanner] = useState<Banner>(null);
  const importBtn = useRef<HTMLButtonElement>(null);

  const doExport = () => {
    const now = new Date();
    const text = JSON.stringify({ ...toPayload(data), exportedAt: now.toISOString() }, null, 2);
    try {
      downloadJson(copy.fileName(now.toISOString().slice(0, 10)), text);
    } catch {
      setBanner({ kind: "error", text: copy.errors.downloadFailed });
    }
  };

  const closePanel = () => {
    setPanelOpen(false);
    setStatus("");
    importBtn.current?.focus();
  };

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setStatus("");
    if (file.size > MAX_IMPORT_BYTES) return setStatus(copy.errors.tooLarge);
    try {
      setBanner({ kind: "confirm", incoming: parseIncoming(await file.text()), source: copy.fileSource(file.name) });
    } catch (err) {
      setStatus(importErrorText(err));
    }
  };

  const confirm = (mode: "replace" | "add") => {
    if (banner?.kind !== "confirm") return;
    const { data: next, note } = applyImport(data, banner.incoming, mode);
    onImport(next);
    setPanelOpen(false);
    setBanner({ kind: "note", text: note });
  };

  const renderBanner = () => {
    if (!banner) return null;
    if (banner.kind !== "confirm") {
      return (
        <div className={"banner" + (banner.kind === "error" ? " error" : "")} role="status">
          <span>{banner.text}</span>
          <span className="actions">
            {/* This banner appears in response to an import the person just confirmed,
                so focus belongs on its dismiss button rather than left behind on a
                control that has gone away. */}
            {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
            <button type="button" className="btn quiet" autoFocus onClick={() => setBanner(null)}>
              {copy.ok}
            </button>
          </span>
        </div>
      );
    }
    const inc = banner.incoming;
    const matches =
      mergeById(data.vehicles, inc.vehicles).updated + mergeById(data.profiles, inc.profiles).updated;
    // A prompt rather than an announcement: focus moves here, so it isn't a live region.
    return (
      <section className="banner" aria-label={copy.previewLabel}>
        <span>
          <b>{banner.source}:</b> {describe(inc)}.
          {matches > 0 && copy.matchesNote(matches)}
          {inc.distanceUnit !== data.distanceUnit && copy.unitNote(data.distanceUnit)}
          {inc.currency !== data.currency &&
            copy.currencyNote(CURRENCY_SYMBOLS[inc.currency], CURRENCY_SYMBOLS[data.currency])}
        </span>
        <span className="actions">
          {/* This prompt appears in response to the person choosing a file and asks a
              question they have to answer, so focus moves to the default answer. */}
          {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
          <button type="button" className="btn primary" autoFocus onClick={() => confirm("replace")}>
            {copy.replace}
          </button>
          <button type="button" className="btn" onClick={() => confirm("add")}>
            {copy.add}
          </button>
          <button type="button" className="btn quiet" onClick={() => setBanner(null)}>
            {copy.cancel}
          </button>
        </span>
      </section>
    );
  };

  const controls = (
    <div className="segmented" role="group" aria-label={copy.groupLabel}>
      <button type="button" onClick={doExport}>
        {copy.exportButton}
      </button>
      <button
        type="button"
        ref={importBtn}
        aria-expanded={panelOpen}
        aria-controls="dataPanel"
        onClick={() => (panelOpen ? closePanel() : setPanelOpen(true))}
      >
        {copy.importButton}
      </button>
    </div>
  );

  const panels = (
    <>
      {renderBanner()}
      {panelOpen && (
        /* Escape closes the panel from wherever focus is inside it. The handler is
           delegated from the panel's own controls, which are all buttons and inputs. */
        /* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */
        <section
          className="panel data-panel"
          id="dataPanel"
          aria-labelledby="dataPanelTitle"
          onKeyDown={(e) => e.key === "Escape" && closePanel()}
        >
          <div className="data-panel-head">
            <h2 id="dataPanelTitle">{copy.importHeading}</h2>
            <button type="button" className="btn quiet" onClick={closePanel}>
              {copy.close}
            </button>
          </div>
          <p className="data-hint">{copy.hint}</p>
          <div className="data-row">
            <label className="btn file-btn">
              {copy.chooseFile}
              {/* The panel opened because the person pressed Import, and choosing a
                  file is the one thing there is to do in it. */}
              <input
                type="file"
                accept=".json,application/json"
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                onChange={(e) => {
                  void onFile(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
            </label>
          </div>
          <p className={"data-status" + (status ? " error" : "")} role="status">
            {status}
          </p>
        </section>
      )}
    </>
  );

  return { controls, panels };
}
